import { Route } from '@angular/router'
import { _numberEnumValues } from '@naturalcycles/js-lib'
import {
  BreakpointAction,
  ChoiceFunction,
  MedicalConditionsBreakpointOption,
  QuizDataInput,
} from '@naturalcycles/shared'
import { allQuizSections } from '@src/app/constants/quiz.js'
import {
  addPageAtEndOfSection,
  addPageInSectionAfterPage,
  filterPagesFromSection,
} from '@src/app/core/util/quiz.util.js'
import { Page } from '@src/app/shared/typings/enum/pages.js'
import { DefaultSections } from '@src/app/shared/typings/enum/quiz.js'

export const ab355Routes: Route[] = [
  {
    path: Page.cycleChangesMood,
    loadComponent: () => import('./mood/mood.page.component').then(m => m.MoodPageComponent),
  },
  {
    path: Page.cycleChangesMoodInfo,
    loadComponent: () =>
      import('./mood-info/mood-info.page.component').then(m => m.MoodInfoPageComponent),
  },
  {
    path: Page.cycleChangesPainExperience,
    loadComponent: () =>
      import('./pain-experience/pain-experience.page.component').then(
        m => m.PainExperiencePageComponent,
      ),
  },
  {
    path: Page.cycleChangesPainInfo,
    loadComponent: () =>
      import('./pain-info/pain-info.page.component').then(m => m.PainInfoPageComponent),
  },
  {
    path: Page.cycleChangesSexDrive,
    loadComponent: () =>
      import('./sex-drive/sex-drive.page.component').then(m => m.SexDrivePageComponent),
  },
  {
    path: Page.cycleChangesSexDriveInfo,
    loadComponent: () =>
      import('./sex-drive-info/sex-drive-info.page.component').then(
        m => m.SexDriveInfoPageComponent,
      ),
  },
  {
    path: Page.cycleChangesMucus,
    loadComponent: () => import('./mucus/mucus.page.component').then(m => m.MucusPageComponent),
  },
  {
    path: Page.cycleChangesSexAndMucusInfo,
    loadComponent: () =>
      import('./sex-and-cervical-mucus-info/sex-and-cervical-mucus-info.page.component').then(
        m => m.SexAndCervicalMucusInfoPageComponent,
      ),
  },
  {
    path: Page.cycleChangesSkinExperience,
    loadComponent: () =>
      import('./skin-experience/skin-experience.page.component').then(
        m => m.SkinExperiencePageComponent,
      ),
  },
  {
    path: Page.cycleChangesSkinInfo,
    loadComponent: () =>
      import('./skin-info/skin-info.page.component').then(m => m.SkinInfoPageComponent),
  },
  {
    path: Page.cycleChangesThankYou,
    loadComponent: () =>
      import('./cycle-changes-thank-you/cycle-changes-thank-you.page.component').then(
        m => m.CycleChangesThankYouPageComponent,
      ),
  },
].map(r => ({
  ...r,
  data: {
    page: r.path,
  },
}))

export const sectionCycleChangesAb355Test = [
  Page.emotionalChanges, // Will be modified for the test
  Page.cycleChangesMood,
  Page.cycleChangesPainExperience,
  Page.cycleChangesPainInfo,
  Page.cycleChangesSexDrive,
  Page.cycleChangesMucus,
  Page.cycleChangesSexAndMucusInfo,
  Page.cycleChangesSkinExperience,
  Page.cycleChangesSkinInfo,
  Page.cycleChangesThankYou,
]

export const sectionCycleChangesAb355Test2 = [
  Page.emotionalChanges, // Will be modified for the test
  Page.cycleChangesMood,
  Page.cycleChangesMoodInfo,
  Page.cycleChangesPainExperience,
  Page.cycleChangesPainInfo,
  Page.cycleChangesSexDrive,
  Page.cycleChangesSexDriveInfo,
  Page.cycleChangesMucus,
  Page.cycleChangesSexAndMucusInfo,
  Page.cycleChangesSkinExperience,
  Page.cycleChangesSkinInfo,
  Page.cycleChangesThankYou,
]

export enum CycleChangesMoodBreakpointOption {
  aLotOfMoodSwings = 1,
  somewhat = 2,
  notMuch = 3,
  skip = 4,
}

export enum CycleChangesSexDriveBreakpointOption {
  variesALot = 1,
  aLittleBit = 2,
  notAtAll = 3,
  haventNoticed = 4,
  skip = 5,
}

export enum CycleChangesPainBreakpointOption {
  cramps = 1,
  backache = 2,
  soreBreasts = 3,
  headache = 4,
  ovulationPain = 5,
  skip = 6,
}

export enum CycleChangesCervicalMucusBreakpointOption {
  sticky = 1,
  creamy = 2,
  light = 3,
  medium = 4,
  heavy = 5,
  watery = 6,
  eggWhite = 7,
  skip = 8,
}

export enum CycleChangesSkinBreakpointOption {
  dryness = 1,
  glowy = 2,
  oiliness = 3,
  acne = 4,
  puffiness = 5,
  skip = 6,
}

export type Ab355QuizDataInput = QuizDataInput & {
  cycleChangesMood?: CycleChangesMoodBreakpointOption
  cycleChangesSexDrive?: CycleChangesSexDriveBreakpointOption
  cycleChangesPain?: CycleChangesPainBreakpointOption
  cycleChangesCervicalMucus?: CycleChangesCervicalMucusBreakpointOption
  cycleChangesSkin?: CycleChangesSkinBreakpointOption
}

const addAb355InfoPage = (flow: Page[][], page: Page, infoPage: Page): Page[][] => {
  if (!flow[allQuizSections.cycleChanges]?.includes(page)) {
    return addPageAtEndOfSection(flow, page, DefaultSections.cycleChanges)
  }
  return addPageInSectionAfterPage(flow, infoPage, page, DefaultSections.cycleChanges)
}

const getDefaultAb355InfoTransformFunction = <T extends number>(
  page: Page,
  infoPage: Page,
  dataKey: keyof Ab355QuizDataInput,
  skipOption: T,
) => {
  return (flow: Page[][], data: Ab355QuizDataInput) => {
    const value = data[dataKey]
    if (value === skipOption) {
      return filterPagesFromSection(flow, [infoPage], DefaultSections.cycleChanges)
    }
    return addAb355InfoPage(flow, page, infoPage)
  }
}

const identityTransformFunction = (flow: Page[][]): Page[][] => {
  return flow
}

export const cycleChangesMoodAb355Test1BreakpointAction: BreakpointAction<
  CycleChangesMoodBreakpointOption,
  Page,
  Ab355QuizDataInput
> = Object.fromEntries(
  _numberEnumValues(CycleChangesMoodBreakpointOption).map(value => [
    value,
    identityTransformFunction,
  ]),
)

const cycleChangesMoodAb355Test2TransformFunction = getDefaultAb355InfoTransformFunction(
  Page.cycleChangesMood,
  Page.cycleChangesMoodInfo,
  'cycleChangesMood',
  CycleChangesMoodBreakpointOption.skip,
)

export const cycleChangesMoodAb355Test2BreakpointAction: BreakpointAction<
  CycleChangesMoodBreakpointOption,
  Page,
  Ab355QuizDataInput
> = Object.fromEntries(
  _numberEnumValues(CycleChangesMoodBreakpointOption).map(value => [
    value,
    cycleChangesMoodAb355Test2TransformFunction,
  ]),
)

const cycleChangesPainAb355AnyTestTransformFunction = getDefaultAb355InfoTransformFunction(
  Page.cycleChangesPainExperience,
  Page.cycleChangesPainInfo,
  'cycleChangesPain',
  CycleChangesPainBreakpointOption.skip,
)

export const cycleChangesPainAb355AnyTestBreakpointAction: BreakpointAction<
  CycleChangesPainBreakpointOption,
  Page,
  Ab355QuizDataInput
> = Object.fromEntries(
  _numberEnumValues(CycleChangesPainBreakpointOption).map(value => [
    value,
    cycleChangesPainAb355AnyTestTransformFunction,
  ]),
)

export const cycleChangesSexDriveAb355Test1BreakpointAction: BreakpointAction<
  CycleChangesSexDriveBreakpointOption,
  Page,
  Ab355QuizDataInput
> = Object.fromEntries(
  _numberEnumValues(CycleChangesSexDriveBreakpointOption).map(value => [
    value,
    identityTransformFunction,
  ]),
)

const cycleChangesSexDriveAb355Test2TransformFunction = getDefaultAb355InfoTransformFunction(
  Page.cycleChangesSexDrive,
  Page.cycleChangesSexDriveInfo,
  'cycleChangesSexDrive',
  CycleChangesSexDriveBreakpointOption.skip,
)

export const cycleChangesSexDriveAb355Test2BreakpointAction: BreakpointAction<
  CycleChangesSexDriveBreakpointOption,
  Page,
  Ab355QuizDataInput
> = Object.fromEntries(
  _numberEnumValues(CycleChangesSexDriveBreakpointOption).map(value => [
    value,
    cycleChangesSexDriveAb355Test2TransformFunction,
  ]),
)

const cycleChangesCervicalMucusAb355Test1TransformFunction: ChoiceFunction<
  Page,
  Ab355QuizDataInput
> = (flow, data) => {
  const cervicalMucus = data?.cycleChangesCervicalMucus
  const sexDrive = data?.cycleChangesSexDrive
  if (
    cervicalMucus === CycleChangesCervicalMucusBreakpointOption.skip &&
    sexDrive === CycleChangesSexDriveBreakpointOption.skip
  ) {
    return filterPagesFromSection(
      flow,
      [Page.cycleChangesSexAndMucusInfo],
      DefaultSections.cycleChanges,
    )
  }
  return addAb355InfoPage(flow, Page.cycleChangesMucus, Page.cycleChangesSexAndMucusInfo)
}

export const cycleChangesCervicalMucusAb355Test1BreakpointAction: BreakpointAction<
  CycleChangesCervicalMucusBreakpointOption,
  Page,
  Ab355QuizDataInput
> = Object.fromEntries(
  _numberEnumValues(CycleChangesCervicalMucusBreakpointOption).map(value => [
    value,
    cycleChangesCervicalMucusAb355Test1TransformFunction,
  ]),
)

const cycleChangesCervicalMucusAb355Test2TransformFunction = getDefaultAb355InfoTransformFunction(
  Page.cycleChangesMucus,
  Page.cycleChangesSexAndMucusInfo,
  'cycleChangesCervicalMucus',
  CycleChangesCervicalMucusBreakpointOption.skip,
)

export const cycleChangesCervicalMucusAb355Test2BreakpointAction: BreakpointAction<
  CycleChangesCervicalMucusBreakpointOption,
  Page,
  Ab355QuizDataInput
> = Object.fromEntries(
  _numberEnumValues(CycleChangesCervicalMucusBreakpointOption).map(value => [
    value,
    cycleChangesCervicalMucusAb355Test2TransformFunction,
  ]),
)

const cycleChangesSkinAb355AnyTestTransformFunction = getDefaultAb355InfoTransformFunction(
  Page.cycleChangesSkinExperience,
  Page.cycleChangesSkinInfo,
  'cycleChangesSkin',
  CycleChangesSkinBreakpointOption.skip,
)

export const cycleChangesSkinAb355AnyTestBreakpointAction: BreakpointAction<
  CycleChangesSkinBreakpointOption,
  Page,
  Ab355QuizDataInput
> = Object.fromEntries(
  _numberEnumValues(CycleChangesSkinBreakpointOption).map(value => [
    value,
    cycleChangesSkinAb355AnyTestTransformFunction,
  ]),
)

function getAb355TestMedicalConditionsTransformFunction(
  current: ChoiceFunction<Page, Ab355QuizDataInput> | undefined,
): ChoiceFunction<Page, Ab355QuizDataInput> {
  return (flow: Page[][]): Page[][] => {
    flow = current?.(flow) || flow
    flow[allQuizSections.cycleChanges] = sectionCycleChangesAb355Test
    return flow
  }
}

export function getAb355TestMedicalConditionsBreakpointAction(
  current:
    | BreakpointAction<MedicalConditionsBreakpointOption, Page, Ab355QuizDataInput>
    | undefined,
): BreakpointAction<MedicalConditionsBreakpointOption, Page, Ab355QuizDataInput> {
  return Object.fromEntries(
    _numberEnumValues(MedicalConditionsBreakpointOption).map(value => [
      value,
      getAb355TestMedicalConditionsTransformFunction(current?.[value]),
    ]),
  )
}

function getAb355Test2MedicalConditionsTransformFunction(
  current: ChoiceFunction<Page, Ab355QuizDataInput> | undefined,
): ChoiceFunction<Page, Ab355QuizDataInput> {
  return (flow: Page[][]): Page[][] => {
    flow = current?.(flow) || flow
    flow[allQuizSections.cycleChanges] = sectionCycleChangesAb355Test2
    return flow
  }
}

export function getAb355Test2MedicalConditionsBreakpointAction(
  current:
    | BreakpointAction<MedicalConditionsBreakpointOption, Page, Ab355QuizDataInput>
    | undefined,
): BreakpointAction<MedicalConditionsBreakpointOption, Page, Ab355QuizDataInput> {
  return Object.fromEntries(
    _numberEnumValues(MedicalConditionsBreakpointOption).map(value => [
      value,
      getAb355Test2MedicalConditionsTransformFunction(current?.[value]),
    ]),
  )
}
